import React, { useState, useRef } from 'react';
import ShareModal from './SocialShare';
import Modal from 'react-modal';
import {
  EmailShareButton,
  EmailIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinIcon,
  FacebookIcon,
} from 'react-share';
import { FaShareAlt, FaSms } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../../hooks/Helper/useIsMobile';
import { QRCode } from 'react-qrcode-logo';
import { handleCertificationShareButtonClick } from '../../tools/utils';
import { ContactDesignation } from '../../types/contactDesignations';
import { analyticsService } from '../../services/analyticsService';

Modal.setAppElement('#root');

type Step = 'closed' | 'choice' | 'shareAll' | 'customSelect' | 'customShare';

const LinkedInShareDropdown = ({ url, subject, message, certName, issueDate, expiryDate }: { url: string, subject: string, message: string, certName: string, issueDate?: string | null, expiryDate?: string | null }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const getYearMonth = (dateString?: string | null) => {
    if (!dateString) return { year: '', month: '' };
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return { year: '', month: '' };
    return { year: d.getFullYear().toString(), month: (d.getMonth() + 1).toString() };
  };

  const issue = getYearMonth(issueDate);
  const expiry = getYearMonth(expiryDate);

  const socialPostText = `I'm proud to share that I'm certified through CCO Certification!\nIt reflects my proven competency and commitment to the load-handling industry through nationally accredited, third-party certification.\n#CCOCertification #GoldStandard`;

  let linkedInProfileUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certName)}&organizationId=4985674&organizationName=NCCCO&certUrl=${encodeURIComponent(url)}`;
  if (issue.year) linkedInProfileUrl += `&issueYear=${issue.year}`;
  if (issue.month) linkedInProfileUrl += `&issueMonth=${issue.month}`;
  if (expiry.year) linkedInProfileUrl += `&expirationYear=${expiry.year}`;
  if (expiry.month) linkedInProfileUrl += `&expirationMonth=${expiry.month}`;

  return (
    <div
      style={{ position: 'relative', display: 'flex' }}
      tabIndex={-1}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false); }}
    >
      <button
        title="Share on LinkedIn"
        onClick={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
      >
        <LinkedinIcon size={40} round />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          width: '140px',
          overflow: 'hidden'
        }}>
          <div
            onClick={() => {
              analyticsService.handleVCO_Desig_LinkedInClicked();
              setIsOpen(false);
              const desktopUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(socialPostText + '\n' + url)}`;
              const mobileUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent('CCO Certification')}&summary=${encodeURIComponent(socialPostText)}`;

              if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                window.location.href = mobileUrl;
              } else {
                window.open(desktopUrl, '_blank', 'width=600,height=600');
              }
            }}
            style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #eee', width: '100%', cursor: 'pointer', background: 'white' }}
          >
            <span style={{ fontSize: '13px', color: '#c67b05', fontWeight: 600 }}>{t('dashboard.myCertificationCard.shareModal.shareToFeed', 'Share to Feed')}</span>
          </div>
          <a
            href={linkedInProfileUrl}
            target={/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "_self" : "_blank"}
            rel="noreferrer"
            onClick={(e) => {
              analyticsService.handleVCO_Desig_LinkedInClicked();
              if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                e.preventDefault();
                window.location.href = linkedInProfileUrl;
              }
              setIsOpen(false);
            }}
            style={{ padding: '10px', fontSize: '13px', color: '#c67b05', textDecoration: 'none', display: 'block', textAlign: 'center', fontWeight: 600, backgroundColor: '#fafafa' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
          >
            {t('dashboard.myCertificationCard.shareModal.addToProfile', 'Add to Profile')}
          </a>
        </div>
      )}
    </div>
  );
};

interface ShareChoiceModalProps {
  CCOID: string;
  Name: string;
  designations: ContactDesignation[];
}

const ShareChoiceModal: React.FC<ShareChoiceModalProps> = ({ CCOID, Name, designations }) => {
  const [step, setStep] = useState<Step>('closed');
  const shareModalRef = useRef<HTMLDivElement>(null);
  const [selectedCert, setSelectedCert] = useState<string | null>(null);
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const isProd = process.env.REACT_APP_BASE_URL === "https://my.ccocert.org/";
  const fullShareUrl = isProd
    ? `https://verifycco.org/cert/${CCOID}`
    : `https://stg1995.verifycco.org/cert/${CCOID}`;
  const getDesigUrl = (code: string) => isProd
    ? `https://verifycco.org/desig/${CCOID}/${code}`
    : `https://stg1995.verifycco.org/desig/${CCOID}/${code}`;

  // "Share All" message uses the full URL
  const shareAllMessage = t('dashboard.myCertificationCard.shareModal.shareMessage', {
    name: Name,
    url: fullShareUrl,
  });
  const shareTitle = t('dashboard.myCertificationCard.shareModal.shareSubject', { name: Name });

  const selectedDesig = designations.find((d) => d.Code === selectedCert);

  const copyUrl = (code: string, url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedStates((prev) => ({ ...prev, [code]: true }));
      setTimeout(() => setCopiedStates((prev) => ({ ...prev, [code]: false })), 2000);
    });
  };

  const handleSmsAll = () => {
    analyticsService.handleVCO_Desig_SMSClicked();
    window.location.href = `sms:?&body=${encodeURIComponent(shareAllMessage)}`;
  };

  const closeAll = () => {
    setStep('closed');
    setSelectedCert(null);
    setCopiedStates({});
  };

  const overlayStyle: React.CSSProperties = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 20px',
    zIndex: 202,
  };

  const baseContentStyle: React.CSSProperties = {
    position: 'relative',
    padding: '20px',
    borderRadius: '8px',
    inset: 'unset',
    backgroundColor: 'white',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  };

  const narrowModal = {
    overlay: overlayStyle,
    content: { ...baseContentStyle, width: '400px' },
  };

  const wideModal = {
    overlay: overlayStyle,
    content: {
      ...baseContentStyle,
      width: '480px',
      maxHeight: '85vh',
      overflowY: 'auto' as const,
    },
  };

  const CloseButton = () => (
    <button
      onClick={closeAll}
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        fontSize: '1.5rem',
      }}
    >
      &#10005;
    </button>
  );

  const BackButton = ({ onClick }: { onClick: () => void }) => (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#c67b05',
        display: 'block',
        margin: '12px auto 0',
        fontSize: '14px',
      }}
    >
      {t('dashboard.myCertificationCard.shareModal.back')}
    </button>
  );

  /** Sharing icons row — works for both Share All and Share Some */
  const SocialRow = ({
    message,
    subject,
    url,
    certName,
    issueDate,
    expiryDate,
    onSms,
  }: {
    message: string;
    subject: string;
    url: string;
    certName: string;
    issueDate?: string;
    expiryDate?: string;
    onSms?: () => void;
  }) => (
    <div
      style={{
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginBottom: '4px',
      }}
    >
      <EmailShareButton url={''} subject={subject} body={message}
        onClick={() => {
          analyticsService.handleVCO_Desig_EmailClicked();
          window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
        }}>
        <EmailIcon size={40} round />
      </EmailShareButton>
      {isMobile && (
        <button
          style={{ backgroundColor: 'transparent', color: 'gray', border: 'none', cursor: 'pointer' }}
          onClick={onSms ?? handleSmsAll}
        >
          <FaSms size={40} />
        </button>
      )}
      <WhatsappShareButton url={message} onClick={() => analyticsService.handleVCO_Desig_WhatsAppClicked()}>
        <WhatsappIcon size={40} round />
      </WhatsappShareButton>
      <LinkedInShareDropdown url={url} subject={subject} message={message} certName={certName} issueDate={issueDate} expiryDate={expiryDate} />
      <button
        title="Share on Facebook"
        onClick={() => {
          analyticsService.handleVCO_Desig_FacebookClicked();
          const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            window.open(fbUrl, '_blank');
          } else if (/Android/i.test(navigator.userAgent)) {
            window.location.href = fbUrl;
          } else {
            window.open(fbUrl, '_blank', 'width=600,height=600');
          }
        }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <FacebookIcon size={40} round />
      </button>
    </div>
  );

  return (
    <div>
      {/* Trigger button */}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleCertificationShareButtonClick();
          setStep('choice');
        }}
        style={{
          width: '100%',
          marginLeft: '10px',
          height: '40px',
          padding: '10px',
          backgroundColor: '#c67b05',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          zIndex: 200,
        }}
      >
        <FaShareAlt /> {t('dashboard.myCertificationCard.shareModal.shareButton')}
      </a>

      {/* ── Choice modal ── */}
      <Modal isOpen={step === 'choice'} onRequestClose={closeAll} contentLabel="Share Options" style={narrowModal}>
        <CloseButton />
        <h2 style={{ marginBottom: '12px', textAlign: 'center' }}>
          {t('dashboard.myCertificationCard.shareModal.title')}
        </h2>
        <p style={{ textAlign: 'center', color: '#555', marginBottom: '24px', fontSize: '14px' }}>
          {t('dashboard.myCertificationCard.shareModal.howToShare')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={() => {
              analyticsService.handleVCO_Cert_DirectLink_QRClicked();
              setStep('closed');
              setTimeout(() => shareModalRef.current?.querySelector('a')?.click(), 0);
            }}
            style={{
              padding: '14px',
              backgroundColor: '#c67b05',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            {t('dashboard.myCertificationCard.shareModal.shareAll')}
          </button>
          <button
            onClick={() => {
              analyticsService.handleVCO_Desig_DirectLink_QRClicked();
              setStep('customSelect');
            }}
            style={{
              padding: '14px',
              backgroundColor: 'white',
              color: '#c67b05',
              border: '2px solid #c67b05',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            {t('dashboard.myCertificationCard.shareModal.shareSelected')}
          </button>
        </div>
      </Modal>

      {/* ── Share Some – cert selection ── */}
      <Modal
        isOpen={step === 'customSelect'}
        onRequestClose={closeAll}
        contentLabel="Select Certifications"
        style={narrowModal}
      >
        <CloseButton />
        <h2 style={{ marginBottom: '6px', textAlign: 'center' }}>{t('dashboard.myCertificationCard.shareModal.shareSelected')}</h2>
        <p style={{ textAlign: 'center', color: '#555', marginBottom: '16px', fontSize: '14px' }}>
          {t('dashboard.myCertificationCard.shareModal.selectCertifications')}
        </p>

        <div style={{ maxHeight: '260px', overflowY: 'auto', marginBottom: '20px' }}>
          {designations.map((desig) => (
            <label
              key={desig.Code}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 8px',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="designation"
                checked={selectedCert === desig.Code}
                onChange={() => setSelectedCert(desig.Code)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>{desig.Code}</span>
              <span style={{ color: '#888', fontSize: '13px', marginLeft: 'auto' }}>
                {t(`dashboard.myCertificationCard.Statuses.${desig.Status}`, desig.Status)}
              </span>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <button
            onClick={() => setStep('choice')}
            style={{
              padding: '10px 16px',
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            {t('dashboard.myCertificationCard.shareModal.back')}
          </button>
          <button
            onClick={() => { if (selectedCert !== null) setStep('customShare'); }}
            disabled={selectedCert === null}
            style={{
              padding: '10px 20px',
              backgroundColor: selectedCert !== null ? '#c67b05' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: selectedCert !== null ? 'pointer' : 'not-allowed',
              fontWeight: 600,
            }}
          >
            {t('dashboard.myCertificationCard.shareModal.next')}
          </button>
        </div>
      </Modal>

      {/* ── Share Some – per-designation cards ── */}
      <Modal
        isOpen={step === 'customShare'}
        onRequestClose={closeAll}
        contentLabel="Share Selected Certifications"
        style={wideModal}
      >
        <CloseButton />
        <h2 style={{ marginBottom: '4px', textAlign: 'center' }}>{t('dashboard.myCertificationCard.shareModal.shareSelected')}</h2>

        {selectedDesig && (() => {
          const url = getDesigUrl(selectedDesig.Code);
          const certMessage = t('dashboard.myCertificationCard.shareModal.shareMessage', {
            name: Name,
            url,
          });
          const handleCertSms = () => {
            analyticsService.handleVCO_Desig_SMSClicked(); // Track SMS click for specific designation
            window.location.href = `sms:?&body=${encodeURIComponent(certMessage)}`;
          };
          return (
            <div
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                padding: '14px',
                backgroundColor: '#fafafa',
                marginTop: '16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <span
                  style={{
                    backgroundColor: '#c67b05',
                    color: 'white',
                    padding: '3px 12px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {selectedDesig.Code}
                </span>
                <span style={{ color: '#666', fontSize: '13px' }}>{t(`dashboard.myCertificationCard.Statuses.${selectedDesig.Status}`, selectedDesig.Status)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <QRCode
                  logoImage="/static/media/Logo_CCO_2.c0fa8490d1c7b4bb6278.png"
                  logoWidth={30}
                  logoHeight={30}
                  value={url}
                  size={100}
                  qrStyle="dots"
                />
              </div>

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={url}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '8px 75px 8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '12px',
                    backgroundColor: 'white',
                  }}
                />
                <button
                  onClick={() => copyUrl(selectedDesig.Code, url)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px 10px',
                    backgroundColor: '#c67b05',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('dashboard.myCertificationCard.shareModal.copyButton')}
                </button>
              </div>
              {copiedStates[selectedDesig.Code] && (
                <p style={{ color: 'green', fontSize: '0.9rem', textAlign: 'center', margin: '6px 0 0' }}>
                  {t('dashboard.myCertificationCard.shareModal.copiedMessage')}
                </p>
              )}

              <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                <SocialRow
                  message={certMessage}
                  subject={shareTitle}
                  url={url}
                  certName={(selectedDesig.PublicDisplayLabel || selectedDesig.Code)}
                  issueDate={selectedDesig.IssueDate}
                  expiryDate={selectedDesig.CertExpirationDate}
                  onSms={handleCertSms}
                />
              </div>
            </div>
          );
        })()}

        <BackButton onClick={() => { setCopiedStates({}); setStep('customSelect'); }} />
      </Modal>

      {/* Hidden ShareModal — triggered programmatically by "Share All" */}
      <div ref={shareModalRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        <ShareModal CCOID={CCOID} Name={Name} />
      </div>
    </div>
  );
};

export default ShareChoiceModal;
